/* eslint-disable camelcase */

import {t} from '@lingui/macro';
import React, {FC, useCallback, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputElement} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {InputTypesEnum} from '../../../../libs/shared/ui/src/components/inputs/input';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fonts, fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {useDebounce} from '../../../../srcCommon/helpers/hookHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {
  SettingsSearchItemModel,
  SettingsSearchItemTypesEnum,
  SettingsSearchPayloadModel,
} from '../../../models/api/settingsSearchModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {ResourceMap} from '../../../store/appTypes';
import {RuleParametersDependencies} from '../../rules/ruleConditionModel';
import {computeIsRuleRecipeBundle} from '../../rules/ruleHelpers';
import {SettingsSearchCommonProps} from './settingsSearchCommonTypes';
import {SettingsSearchDropdown} from './settingsSearchDropdown';

/*
 * Props.
 */

interface SettingsTopbarSearchProps extends SettingsSearchCommonProps {
  user: TeammateModel;
  teamsMap: ResourceMap<TeamModel>;
  ruleParametersDependencies: RuleParametersDependencies;
}

/*
 * Style.
 */

const StyledSearchInputElement = styled(BoxedInputElement)`
  margin-left: 0;
  font-family: ${fonts.system};
  font-size: ${fontSizes.medium};

  &::placeholder {
    color: ${(p) => p.theme.greys.shade70};
  }
`;

export const StyledExitButton = styled(ChromelessButton)`
  padding-right: 8px;
  flex-shrink: 0;
  ${center()};
  width: 30px;
  cursor: default;

  svg {
    color: ${(p) => p.theme.greys.shade60} !important;
  }

  &:hover svg {
    color: ${(p) => p.theme.greys.shade70} !important;
  }
`;

/*
 * Component.
 */

interface SearchInputWrapperProps extends SettingsTopbarSearchProps {
  className?: string;
}

const SearchInputWrapper: FC<SearchInputWrapperProps> = (props) => {
  const {
    className,
    user,
    makeSettingsSearch,
    trackSearchQuery,
    trackSearchResultClick,
    onSeeAllClick,
    onSelectItem,
  } = props;
  const boxRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SettingsSearchPayloadModel | undefined>(undefined);
  const [isStillLoading, setIsStillLoading] = useState(false);

  const limitedSearchResults = useMemo(() => {
    if (!searchResults) {
      return searchResults;
    }

    return limitNumbersOfResults(searchResults);
  }, [searchResults]);

  const resetState = useCallback(() => {
    setIsDropdownOpen(false);
    setIsStillLoading(false);
    setSearchQuery('');
    setSearchResults(undefined);
  }, []);

  const reportSearchQuery = useDebounce((query: string, resultsCount: number) => {
    trackSearchQuery({
      query,
      results_count: resultsCount,
    });
  }, 500);

  const onInputChange = useDebounce((query: string) => {
    if (!query) {
      setSearchResults(undefined);
      return;
    }

    const loadingTimeout = setTimeout(() => {
      setIsStillLoading(true);
    }, 500);

    makeSettingsSearch(query).then((results) => {
      reportSearchQuery(
        query,
        results.map((i) => i.items.length).reduce((a, b) => a + b, 0),
      );
      clearTimeout(loadingTimeout);
      setIsStillLoading(false);
      setSearchResults(results);
      setIsDropdownOpen(true);
    });
  }, 200);

  const maybeRenderDropdown = useCallback(() => {
    if (!isDropdownOpen || !boxRef.current || (!limitedSearchResults && !isStillLoading)) {
      return null;
    }

    return (
      <SettingsSearchDropdown
        ruleParametersDependencies={props.ruleParametersDependencies}
        isStillLoading={isStillLoading}
        onRequestClose={() => setIsDropdownOpen(false)}
        anchor={boxRef}
        searchResults={limitedSearchResults}
        user={user}
        onSeeAllClick={(resourceType) => {
          resetState();
          trackSearchResultClick({
            click_type: 'shortcut',
            resource_id: null,
            shortcut_type: resourceType,
          });
          onSeeAllClick(resourceType);
        }}
        onSelectItem={(payload, index) => {
          resetState();
          trackSearchResultClick({
            click_type: payload.resourceType,
            ...getItemValueObject(payload, index),
          });
          onSelectItem(payload, index);
        }}
        teamsMap={props.teamsMap}
      />
    );
  }, [
    isDropdownOpen,
    isStillLoading,
    limitedSearchResults,
    onSeeAllClick,
    onSelectItem,
    props.ruleParametersDependencies,
    props.teamsMap,
    resetState,
    trackSearchResultClick,
    user,
  ]);

  const localize = useLocalize();

  return (
    <span {...buildInteractionIdProps('settings-search-input')}>
      <BoxedInput noMargin size={VisualSizesEnum.LARGE} boxClassName={className} boxRef={boxRef}>
        {(childProps) => (
          <>
            <BoxedInputIcon {...childProps} iconName="searchSmall" />
            <StyledSearchInputElement
              {...childProps}
              onChange={(newQuery) => {
                setSearchQuery(newQuery);
                onInputChange(newQuery);
              }}
              type={InputTypesEnum.TEXT}
              hasSpellCheck={false}
              value={searchQuery}
              placeholder={localize(
                t('settingssearch-placeholder')`Search teammates, inboxes, rules, tags, and more`,
              )}
              onFocus={() => setIsDropdownOpen(true)}
              shouldFocus
            />
            {searchQuery && (
              <StyledExitButton onClick={resetState}>
                <Icon name="exitFilledSmall" />
              </StyledExitButton>
            )}
          </>
        )}
      </BoxedInput>
      {maybeRenderDropdown()}
    </span>
  );
};

const StyledSearchInput = styled(SearchInputWrapper)`
  border-radius: 8px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray20};
  }

  & {
    background-color: ${(p) => p.theme.alphas.gray10};
  }

  &:focus-within {
    background-color: ${(p) => p.theme.greys.white};
  }

  & > div:first-child {
    margin-left: 5px;
    margin-right: 1px;
  }

  svg {
    color: ${(p) => p.theme.greys.shade70};
  }
`;

export const SettingsTopbarSearch: FC<SettingsTopbarSearchProps> = (props) => (
  <StyledSearchInput {...props} />
);

/*
 * Helpers.
 */

//  This method makes sure we never display more than 14 items.
const ITEMS_UPPER_LIMIT = 14;
function limitNumbersOfResults(results: SettingsSearchPayloadModel) {
  // Compute a maximum number of items.
  const MAX_ITEMS = Math.max(ITEMS_UPPER_LIMIT - results.length, results.length);

  // 1. Compute the total amount of items.
  let total = results.map((i) => i.items.length).reduce((a, b) => a + b, 0);
  const finalResults = [...results];

  // 2. Loop as long as the total is above our limit
  while (total > MAX_ITEMS) {
    // eslint-disable-next-line no-loop-func
    finalResults.forEach((payload, index) => {
      // If we have less than two items, no need to cut.
      if (payload.items.length < 2) {
        return;
      }

      // Create a new array of items with the last item removed.
      const newItems = [...payload.items];
      newItems.pop();
      total -= 1;

      // Insert our new array in the final array
      finalResults[index] = {
        ...payload,
        items: newItems,
      } as SettingsSearchItemModel;
    });
  }

  return finalResults;
}

function getItemValueObject(
  payloadItem: SettingsSearchItemModel,
  itemIndex: number,
): {resource_id: string | number | null; shortcut_type: string | null} {
  switch (payloadItem.resourceType) {
    case SettingsSearchItemTypesEnum.ANSWER:
    case SettingsSearchItemTypesEnum.CUSTOM_VIEW:
    case SettingsSearchItemTypesEnum.PLUGIN:
    case SettingsSearchItemTypesEnum.SIGNATURE:
    case SettingsSearchItemTypesEnum.CHANNEL:
    case SettingsSearchItemTypesEnum.TEAMMATE: {
      return {
        resource_id: payloadItem.items[itemIndex].id || null,
        shortcut_type: null,
      };
    }

    case SettingsSearchItemTypesEnum.RULE: {
      return {
        resource_id:
          (computeIsRuleRecipeBundle(payloadItem.items[itemIndex].ruleBundleType)
            ? payloadItem.items[itemIndex].ruleBundleId
            : payloadItem.items[itemIndex].rule.id) || null,
        shortcut_type: null,
      };
    }

    case SettingsSearchItemTypesEnum.TEAM: {
      return {
        resource_id: payloadItem.items[itemIndex].team.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.INBOX: {
      return {
        resource_id: payloadItem.items[itemIndex].inbox.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.TAG: {
      return {
        resource_id: payloadItem.items[itemIndex].tag.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.ROLE: {
      return {
        resource_id: payloadItem.items[itemIndex].role.alias,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.VACATION_RESPONDER: {
      return {
        resource_id: payloadItem.items[itemIndex].vacationResponder.id,
        shortcut_type: null,
      };
    }
    case SettingsSearchItemTypesEnum.SETTINGS_ROUTE:
    case SettingsSearchItemTypesEnum.TEAMMATE_ROUTE:
    case SettingsSearchItemTypesEnum.IMPORT_ROUTE:
    case SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE:
    case SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE:
    case SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE: {
      return {
        resource_id: null,
        shortcut_type: payloadItem.items[itemIndex].key,
      };
    }

    case SettingsSearchItemTypesEnum.INTEGRATION: {
      return {
        resource_id: null,
        shortcut_type: payloadItem.items[itemIndex].type,
      };
    }

    default: {
      return {
        resource_id: null,
        shortcut_type: null,
      };
    }
  }
}
